import { useEffect, useState } from 'react'
import { getCountryCode } from 'src/business/services'
import {
  EVENT_TEMPLATE_DATA,
  EVENT_SUBTYPE_INDIVIDUAL_THERAPY,
  SESSION_PLATFORM_HOME_VISIT,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_ONLINE,
  allAvailableEventTypes,
} from 'src/consumer/constants'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { getDurationOptions } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'

const VALID_MEETING_SRC_VALUES = new Set([
  SESSION_PLATFORM_ONLINE,
  SESSION_PLATFORM_OFFLINE,
  SESSION_PLATFORM_HOME_VISIT,
])

function normalizeInboundEventFormData(data) {
  if (!data) return null
  const clone = { ...data }

  if (!VALID_MEETING_SRC_VALUES.has(clone.meetingSrc)) {
    clone.meetingSrc = SESSION_PLATFORM_ONLINE
  }

  const rawAssessments = clone.psychAssessmentIds ?? clone.clinicalAssessmentIds
  if (Array.isArray(rawAssessments) && rawAssessments.length) {
    clone.psychAssessmentIds = rawAssessments.map((item) => {
      if (item && typeof item === 'object') {
        const value = item.value ?? item.id
        const label = item.label ?? item.title ?? item.name ?? value
        if (value) {
          return { value, label }
        }
      }
      if (typeof item === 'string') {
        return { value: item, label: item }
      }
      return item
    })
  } else {
    clone.psychAssessmentIds = Array.isArray(clone.psychAssessmentIds) ? clone.psychAssessmentIds : []
  }

  if (clone.type === allAvailableEventTypes.webinar) {
    if (!clone.pricingPlanId && clone.subscriptionPlanId) {
      clone.pricingPlanId = clone.subscriptionPlanId
    }
  }

  if (clone.type === allAvailableEventTypes.therapySession && clone.subtype == null) {
    clone.subtype = EVENT_SUBTYPE_INDIVIDUAL_THERAPY
  }

  const legacyScreening = clone.screeningType
  if (
    clone.type === allAvailableEventTypes.screening &&
    legacyScreening &&
    !clone.targetSpecialtySlug
  ) {
    clone.targetSpecialtySlug = legacyScreening
  }

  if (clone.screeningAllowedIfPossible === undefined || clone.screeningAllowedIfPossible === null) {
    clone.screeningAllowedIfPossible = true
  }

  return clone
}

export default function useEventFormState({
  eventFormModalData,
  oneOnOnePlans,
  mentorClinicAssociationData,
  mentorProfileData,
  newEventDefaults,
}) {
  const [selectedPanelMembers, setSelectedPanelMembers] = useState([])
  const [loadingPanelMembers, setLoadingPanelMembers] = useState(true)
  const [isCustomTimeChosen, setIsCustomTimeChosen] = useState(false)
  const [viewPriceCountry, setViewPriceCountry] = useState()
  const [showAddCustomPrice, setShowAddCustomPrice] = useState(false)
  const [oneOnOnePlanToEdit, setOneOnOnePlanToEdit] = useState()
  const [bundlePackToEdit, setBundlePackToEdit] = useState()
  const [showBundlePackEditModal, setShowBundlePackEditModal] = useState(false)

  const isExistingEventFormData = Boolean(eventFormModalData?.uid || eventFormModalData?.id)

  function normalizePriceValue(value) {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : null
  }

  function priceValueMatches(left, right) {
    const leftValue = normalizePriceValue(left)
    const rightValue = normalizePriceValue(right)
    return leftValue !== null && rightValue !== null && leftValue === rightValue
  }

  function pricingDetailMatchesDefaults(pricingDetail, defaultPricing) {
    if (!pricingDetail || !defaultPricing) {
      return false
    }
    const pricingCurrency = (pricingDetail.currencyId || pricingDetail.currency || '').toLowerCase()
    const defaultCurrency = (defaultPricing.currencyId || '').toLowerCase()
    const currencyMatches =
      !defaultCurrency ||
      pricingCurrency === defaultCurrency
    const discountedPrice = pricingDetail.discountedPrice ?? pricingDetail.finalPrice
    return (
      currencyMatches &&
      priceValueMatches(pricingDetail.price, defaultPricing.price) &&
      priceValueMatches(discountedPrice, defaultPricing.discountedPrice) &&
      priceValueMatches(pricingDetail.introductoryPrice, defaultPricing.introductoryPrice)
    )
  }

  function planMatchesDefaultPricing(plan, defaultPricing) {
    if (!plan || !defaultPricing) {
      return false
    }
    const matrixEntries = Object.values(plan?.pricingMatrix || {}).filter(Boolean)
    if (matrixEntries.some((pricingDetail) => pricingDetailMatchesDefaults(pricingDetail, defaultPricing))) {
      return true
    }
    return pricingDetailMatchesDefaults(plan, defaultPricing)
  }

  function findDefaultPlanId(plans = oneOnOnePlans) {
    const defaultPricing = newEventDefaults?.pricing
    if (!defaultPricing) {
      return plans?.[0]?.id
    }
    const eligiblePlans = (plans || []).filter(
      (plan) => plan?.mentorId === mentorProfileData?.id || !plan?.mentorId,
    )
    return (
      eligiblePlans.find((plan) => planMatchesDefaultPricing(plan, defaultPricing))?.id ||
      eligiblePlans?.[0]?.id ||
      plans?.[0]?.id
    )
  }

  function buildNewEventData(seedData = {}) {
    const defaultPlanId = findDefaultPlanId()
    const eventData = {
      ...EVENT_TEMPLATE_DATA,
      ...(newEventDefaults?.event || {}),
      ...seedData,
      ...(defaultPlanId ? { subscriptionPlanId: defaultPlanId } : {}),
    }

    if (eventData.type === allAvailableEventTypes.therapySession && eventData.subtype == null) {
      eventData.subtype = EVENT_SUBTYPE_INDIVIDUAL_THERAPY
    }
    if (eventData.type === allAvailableEventTypes.screening) {
      eventData.screeningAllowedIfPossible =
        eventData.screeningAllowedIfPossible !== undefined
          ? eventData.screeningAllowedIfPossible
          : true
    }

    return eventData
  }

  const [basicEventObject, setBasicEventObject] = useState(
    isExistingEventFormData
      ? normalizeInboundEventFormData(eventFormModalData)
      : buildNewEventData(eventFormModalData || {}),
  )

  useEffect(() => {
    if (isExistingEventFormData) {
      setBasicEventObject(normalizeInboundEventFormData(eventFormModalData))
    } else {
      setBasicEventObject(buildNewEventData(eventFormModalData || {}))
    }
  }, [eventFormModalData, isExistingEventFormData]) // eslint-disable-line react-hooks/exhaustive-deps -- reset only when switching rows

  useEffect(() => {
    if (isExistingEventFormData || !oneOnOnePlans?.[0]?.id) {
      return
    }
    setBasicEventObject((prev) => {
      const defaultPlanId = findDefaultPlanId(oneOnOnePlans)
      if (!defaultPlanId) {
        return prev
      }
      if (prev.subscriptionPlanId === defaultPlanId || prev.pricingPlanId === defaultPlanId) {
        return prev
      }
      const currentPlanIsUserSelected =
        (prev.subscriptionPlanId || prev.pricingPlanId) &&
        prev.subscriptionPlanId !== oneOnOnePlans?.[0]?.id &&
        prev.pricingPlanId !== oneOnOnePlans?.[0]?.id
      if (currentPlanIsUserSelected) {
        return prev
      }
      return { ...prev, subscriptionPlanId: defaultPlanId }
    })
  }, [oneOnOnePlans, isExistingEventFormData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentCountryCode = newEventDefaults?.viewPriceCountry || getCountryCode() || 'IN'
    setViewPriceCountry(currentCountryCode.toLowerCase())
  }, [newEventDefaults?.viewPriceCountry])

  const panelIdsKey = (basicEventObject?.panelIds || []).filter(Boolean).join(',')

  useEffect(() => {
    let cancelled = false

    async function loadPanelMembers() {
      if (!panelIdsKey) {
        setSelectedPanelMembers([])
        setLoadingPanelMembers(false)
        return
      }
      setLoadingPanelMembers(true)
      const response = await getMPDataWithoutParams(
        `/api/secure/teacher/other-teachers/list?ids=${panelIdsKey}`,
      )
      if (cancelled) {
        return
      }
      if (response.status === 200) {
        const ids = panelIdsKey ? panelIdsKey.split(',') : []
        const toSet = ids.map((id) => {
          const foundTeacher = response.data.find((teacher) => teacher.id === id) || {
            id,
            name: 'Unknown',
          }
          return foundTeacher
        })
        setSelectedPanelMembers(toSet)
      }
      setLoadingPanelMembers(false)
    }

    loadPanelMembers()
    return () => {
      cancelled = true
    }
  }, [panelIdsKey])

  function changeLocationBasedOnMentorOrClinicLocation(locationObject, contextId) {
    const contextIdToUse = contextId
    if (contextIdToUse) {
      const foundClinic = mentorClinicAssociationData?.find(
        (clinic) => clinic?.id === contextIdToUse,
      )
      setBasicEventObject((prev) => ({
        ...prev,
        location: locationObject ? locationObject : foundClinic.location,
      }))
    } else {
      setBasicEventObject((prev) => ({
        ...prev,
        location: locationObject ? locationObject : mentorProfileData?.location,
      }))
    }
  }

  useEffect(() => {
    changeLocationBasedOnMentorOrClinicLocation(
      basicEventObject?.location,
      basicEventObject?.contextId,
    )
  }, [mentorClinicAssociationData, mentorProfileData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const allDurations = getDurationOptions()
    if (
      !allDurations?.some(
        (item) => parseInt(item?.props?.value, 10) === basicEventObject?.durationInMins,
      )
    ) {
      setIsCustomTimeChosen(true)
    }
  }, [basicEventObject?.durationInMins])

  return {
    basicEventObject,
    setBasicEventObject,
    selectedPanelMembers,
    setSelectedPanelMembers,
    loadingPanelMembers,
    setLoadingPanelMembers,
    isCustomTimeChosen,
    setIsCustomTimeChosen,
    viewPriceCountry,
    setViewPriceCountry,
    showAddCustomPrice,
    setShowAddCustomPrice,
    oneOnOnePlanToEdit,
    setOneOnOnePlanToEdit,
    defaultSubscriptionPlanId: findDefaultPlanId(),
    bundlePackToEdit,
    setBundlePackToEdit,
    showBundlePackEditModal,
    setShowBundlePackEditModal,
    changeLocationBasedOnMentorOrClinicLocation,
  }
}
